import { ethers } from "ethers";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HeaderSubText } from "../../shared/SharedModalStyles/styles";
import { ExtrasText, Padding, SuccessMessage } from "../style";
import {
  HeaderText,
  InfoRow,
} from "./style";

import {
  IsUSDC,
} from "../../../constants/addresses";
import {
  ONCHAIN_ESTIMATES,
  formatOnchainEstimate,
} from "../../../constants/onchainEstimates";
import { AuthContext } from "../../../contexts/AuthContext";
import { ConnectWalletContext } from "../../../contexts/ConnectWalletContext";
import { useGetAccount } from "../../../hooks/api/account/useGetAccount";
import { useYieldVault } from "../../../hooks/api/yieldVault/useYieldVault";
import { getUsdcDecimals } from "../../../config/chains";
import {
  collateralAssetDecimals,
  getAssetShortName,
} from "../../../utils/asset/assets";
import {
  toTokenUnits,
  truncateDecimalString,
} from "../../../utils/decimalString";
import {
  assetDisplayDecimals,
  formatFlooredAmount,
} from "../../DepositWithdrawModal/WithdrawModal.helpers";
import ModalOverlayInfo from "../../shared/ModalOverlayInfo";
import { IDepositProps } from "./types";
import {
  getCollateralAssetValues,
  getSelectedAssetValues,
} from "./helpers";
import DepositInputSection from "./DepositInputSection";

function Deposit({
  form,
  isDepositing,
  asset,
  onChangeAsset,
  balance,
  maxBalance,
  maxUnavailableReason,
  socketDepositInfo,
  showHelpInfo,
  showApprovalSuccessMessage,
  onHideHelpInfo,
  selectedDepositNetwork,
  onChangeSelectedDepositNetwork,
  customError,
  yieldVaultToggle,
  setYieldVaultToggle,
}: IDepositProps) {
  const { showConnectModal } = useContext(ConnectWalletContext);
  const { account } = useContext(AuthContext);
  const { data: accountData } = useGetAccount();
  const [copied, setCopied] = useState(false);
  const [sliderPercent, setSliderPercent] = useState(0);

  const handleCopyAddress = useCallback(() => {
    if (account) {
      navigator.clipboard.writeText(account);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [account]);
  const { data: yieldVaultData } = useYieldVault();
  const {
    register,
    formState: { errors },
    setValue,
    clearErrors,
  } = form;
  const { t } = useTranslation("app", {
    keyPrefix: "ConnectWalletModal.Deposit",
  });
  const [assetDropdownOpen, setAssetDropdownOpen] = useState(false);
  // The decimals the deposit hook parses this asset's amount with.
  const decimals = collateralAssetDecimals[asset] ?? getUsdcDecimals();
  const exchangeBalance = useMemo(() => {
    const displayDecimals = assetDisplayDecimals(asset);
    const selectedAssetValues = getSelectedAssetValues(
      asset,
      selectedDepositNetwork
    );
    const collateralBalance = accountData?.collaterals?.find(
      (c) =>
        getCollateralAssetValues(c.collateral_asset, selectedDepositNetwork).some(
          (value) => selectedAssetValues.includes(value)
        )
    );
    if (collateralBalance) {
      return formatFlooredAmount(collateralBalance.balance, displayDecimals);
    }
    if (IsUSDC(asset) && accountData?.balance) {
      return formatFlooredAmount(accountData.balance, displayDecimals);
    }
    return "-";
  }, [
    accountData?.balance,
    accountData?.collaterals,
    asset,
    selectedDepositNetwork,
  ]);

  const estDepositTime = useMemo(() => {
    if (socketDepositInfo) {
      return formatOnchainEstimate(ONCHAIN_ESTIMATES.socketDeposit);
    }
    return formatOnchainEstimate(ONCHAIN_ESTIMATES.deposit);
  }, [socketDepositInfo]);

  const notEnoughEthForFees = socketDepositInfo
    ? socketDepositInfo.ethBalance.lt(socketDepositInfo.depositFee || 0)
    : false;

  const messageContent = useMemo(() => {
    const hasErrors =
      notEnoughEthForFees || Boolean(Object.keys(errors).length);
    if (socketDepositInfo && notEnoughEthForFees) {
      return <ExtrasText $type="error">{t("insufficient_balance")}</ExtrasText>;
    }
    if (showApprovalSuccessMessage && !hasErrors) {
      return <SuccessMessage>{t("ready_to_deposit")}</SuccessMessage>;
    }
    return undefined;
  }, [
    errors,
    notEnoughEthForFees,
    showApprovalSuccessMessage,
    socketDepositInfo,
    t,
  ]);

  const onMaxClick = useCallback(() => {
    // Only ever the gas-reserved maximum. Falling back to the raw wallet
    // balance handed back the gas the reserve exists to keep, and filled 0
    // when the balance had not been read.
    if (maxBalance === undefined) return;
    setValue("amount", maxBalance, { shouldValidate: true });
    setSliderPercent(100);
  }, [maxBalance, setValue]);

  const onSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const pct = Number(e.target.value);
      setSliderPercent(pct);
      const max = maxBalance;
      if (max === undefined) return;
      const maxUnits = toTokenUnits(max, decimals);
      if (maxUnits && maxUnits.gt(0)) {
        // Integer math on base units, floored to 6dp: `(bal * pct / 100).toFixed(6)`
        // rounded half-up and could land above the balance.
        const amount =
          pct === 100
            ? max
            : truncateDecimalString(
                ethers.utils.formatUnits(maxUnits.mul(pct).div(100), decimals),
                Math.min(6, decimals)
              ) ?? "0";
        setValue("amount", amount, { shouldValidate: true });
      }
    },
    [decimals, maxBalance, setValue]
  );

  useEffect(() => {
    if (!showConnectModal) {
      setTimeout(() => {
        clearErrors();
        setValue("amount", undefined);
        setSliderPercent(0);
      }, 200);
    }
  }, [showConnectModal, clearErrors, setValue]);

  return (
    <>
      <ModalOverlayInfo show={showHelpInfo} onClose={onHideHelpInfo}>
        <HeaderText>{t("how_deposits_work_header")}</HeaderText>
        <HeaderSubText style={{ padding: 0 }}>
          {t("how_deposits_work_subheader", {
            asset: getAssetShortName(asset),
          })}
        </HeaderSubText>
      </ModalOverlayInfo>
      <Padding>
        <DepositInputSection
          account={account}
          copied={copied}
          handleCopyAddress={handleCopyAddress}
          selectedDepositNetwork={selectedDepositNetwork}
          onChangeSelectedDepositNetwork={onChangeSelectedDepositNetwork}
          assetDropdownOpen={assetDropdownOpen}
          setAssetDropdownOpen={setAssetDropdownOpen}
          asset={asset}
          onChangeAsset={onChangeAsset}
          isDepositing={isDepositing}
          register={register}
          errors={errors}
          customError={customError}
          maxBalance={maxBalance}
          maxUnavailableReason={maxUnavailableReason}
          balance={balance}
          decimals={decimals}
          onMaxClick={onMaxClick}
          sliderPercent={sliderPercent}
          onSliderChange={onSliderChange}
        />

        {socketDepositInfo && (
          <InfoRow error={notEnoughEthForFees}>
            <span>{t("wallet_balance")} (ETH)</span>
            <span>
              {Number(
                ethers.utils.formatEther(socketDepositInfo.ethBalance)
              ).toFixed(4)}
            </span>
          </InfoRow>
        )}
        <InfoRow>
          <span>
            {t("wallet_balance")} (
            {getAssetShortName(asset, selectedDepositNetwork)})
          </span>
          <span>{balance || "---"}</span>
        </InfoRow>
        <InfoRow>
          <span>
            {t("exchange_balance")} ({asset})
          </span>
          <span>{exchangeBalance}</span>
        </InfoRow>
        <InfoRow>
          <span>{t("estimated_deposit_time")}</span>
          <span>{estDepositTime}</span>
        </InfoRow>
        {socketDepositInfo && (
          <InfoRow error={notEnoughEthForFees}>
            <span>{t("bridge_fee")}</span>
            <span>
              {socketDepositInfo.depositFee
                ? `${Number(
                    ethers.utils.formatEther(socketDepositInfo.depositFee)
                  ).toFixed(6)} ETH`
                : "---"}
            </span>
          </InfoRow>
        )}
        {/* {IsUSDC(asset) && !yieldVaultData?.is_paused && (
          <YieldVaultToggle
            isOn={yieldVaultToggle}
            onToggle={() => {
              setYieldVaultToggle(!yieldVaultToggle);
            }}
          />
        )} */}
        {messageContent}
      </Padding>
    </>
  );
}
export default Deposit;
